import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import UnitFinishEarlyDropdown from '../../../components/app-agent/UnitFinishEarlyDropdown';
import { useAuth } from '../../../context/Auth';
import FormInput from '../../../components/FormInput';
import { VehicleChecklistMoment, VehicleChecklistValuesEnum } from '../../../services/central-api/dispatch';
import { ScrollView } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const MIN_CHECKED_ITEMS = 1;

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onClose: () => void;
    onSubmit: (checklist?: CreateUpdateChecklist['checklist']) => void;
    unit?: AppAgentUnit;
    finishEarlyReason: number;
    setFinishEarlyReason: Dispatch<SetStateAction<number>>;
    needFinishReason: boolean;
    unitId: number;
    dailyPoliceLog: string;
    setDailyPoliceLog: Dispatch<SetStateAction<string>>;
    observations: string;
    setObservations: Dispatch<SetStateAction<string>>;
}

export default function FinishUnitModal({ onClose, onSubmit, finishEarlyReason, setFinishEarlyReason, needFinishReason, isModalVisible, setModalVisible, unitId, dailyPoliceLog, setDailyPoliceLog, observations, setObservations, unit }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [step, setStep] = useState<number>(1);
    const [checkedItems, setCheckedItems] = useState<VehicleChecklistValuesEnum[]>([]);

    const checklistItems = useMemo(() => {
        return unit?.externalServiceOrders?.filter(item => item.moment === VehicleChecklistMoment.AFTER_SERVICE) ?? [];
    }, [unit]);

    const hasChecklistStep = checklistItems.length > 0;
    const totalSteps = hasChecklistStep ? 2 : 1;
    const isChecklistStep = hasChecklistStep && step === 1;
    const isLastStep = step >= totalSteps;

    useEffect(() => {
        if (isModalVisible) {
            setStep(1);
            setCheckedItems([]);
        }
    }, [isModalVisible]);

    if (!authenticatedUser || unitId === 0) {
        return;
    }

    function toggleChecklistItem(value: VehicleChecklistValuesEnum) {
        setCheckedItems(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    }

    function buildChecklist(): CreateUpdateChecklist['checklist'] {
        const values = Object.fromEntries(
            checklistItems.map(item => [item.checklistValue, checkedItems.includes(item.checklistValue)])
        ) as Partial<VehicleChecklistValues>;

        return { [VehicleChecklistMoment.AFTER_SERVICE]: values };
    }

    function isSaveButtonDisabled() {
        if (isChecklistStep) {
            return checkedItems.length < MIN_CHECKED_ITEMS;
        }

        if (dailyPoliceLog === '') {
            return true;
        }

        if (needFinishReason && !finishEarlyReason) {
            return true;
        }

        return false;
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
                setFinishEarlyReason(0);
            }}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('unitDeactivation')}</MyAppText>
                        <MyAppText style={styles.subheader}>
                            {totalSteps > 1 ? translate('stepXofY', { x: step, y: totalSteps }) : translate('fillFieldsBelow')}
                        </MyAppText>
                    </View>
                    {isChecklistStep ? (
                        <View style={styles.viewStyle}>
                            <MyAppText style={styles.inputLabelStyle}>{translate('vehicleChecklist')}</MyAppText>
                            <ScrollView style={{ maxHeight: 320 }}>
                                <View style={styles.checklistList}>
                                    {checklistItems.map(item => {
                                        const selected = checkedItems.includes(item.checklistValue);
                                        return (
                                            <TouchableOpacity
                                                key={item.id}
                                                style={styles.checklistRow}
                                                onPress={() => toggleChecklistItem(item.checklistValue)}
                                                accessibilityRole='checkbox'
                                                accessibilityState={{ checked: selected }}
                                            >
                                                <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                                                    {selected && <FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                                                </View>
                                                <MyAppText style={styles.checklistLabel}>{translate(item.checklistValue)}</MyAppText>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                            </ScrollView>
                        </View>
                    ) : (
                        <>
                            {needFinishReason && <View style={[styles.viewStyle, { zIndex: 2 }]}>
                                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                                    <MyAppText style={styles.inputLabelStyle}>{translate('unitDeactivationReason')}</MyAppText>
                                    {needFinishReason ?
                                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                        : null}
                                </View>
                                <UnitFinishEarlyDropdown
                                    label=''
                                    value={finishEarlyReason}
                                    setValue={setFinishEarlyReason}
                                    unitId={unitId}
                                />
                            </View>}
                            <FormInput
                                label={translate('dailyPolicingLog')}
                                placeholder={translate('dailyPolicingLog')}
                                value={dailyPoliceLog}
                                required
                                multiline={true}
                                onChangeText={(val) => setDailyPoliceLog(val)}
                                labelStyle={styles.inputLabelStyle}
                                viewStyle={styles.viewStyle}
                            />

                            <FormInput
                                label={translate('observations')}
                                placeholder={translate('observations')}
                                value={observations}
                                multiline={true}
                                onChangeText={(val) => setObservations(val)}
                                labelStyle={styles.inputLabelStyle}
                                viewStyle={styles.viewStyle}
                            />
                        </>
                    )}

                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        onSubmit={() => {
                            if (!isLastStep) {
                                setStep(step + 1);
                                return;
                            }
                            onSubmit(hasChecklistStep ? buildChecklist() : undefined);
                            setModalVisible(false);
                            onClose();
                        }}
                        onClose={() => {
                            if (step > 1) {
                                setStep(step - 1);
                                return;
                            }
                            setModalVisible(false);
                            onClose();
                        }}
                        {...(hasChecklistStep ? {
                            confirmText: isLastStep ? 'conclude' : 'forward',
                            cancelText: step === 1 ? 'cancel' : 'goBack'
                        } : {})}
                        disabled={isSaveButtonDisabled()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    viewStyle: {
        flexDirection: 'column',
        rowGap: 8
    },
    checklistList: {
        gap: 12,
        paddingLeft: 16
    },
    checklistRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    checklistLabel: {
        fontSize: 16,
        color: theme.colors.labelColor
    },
}));
